import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, IconButton, Tooltip } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SchemaIcon from '@mui/icons-material/Schema';
import { ReactFlowProvider } from 'reactflow';

import {
  ActionsTable,
  CatalogDataGrid,
  CustomBasicOptionsModal,
  GenericModal
} from 'components/shared';
import { useSelectTableItemsToExport } from 'hooks/useSelectTableItemsToExport/useSelectTableItemsToExport';
import CatalogLayout from 'layouts/CatalogLayout';
import { useCatalog } from 'hooks/useCatalog';
import { useApiContext } from 'context/ApiContext';
import { KEYS } from 'helpers/react-query.config';
import {
  genericTranslationColumns,
  genericTranslationConstants,
  genericTranslationHeaders
} from 'helpers/genericTranslationHeaders';
import LoadingFallback from 'routes/LoadingFallback';
import { ProblemFlowSearch } from 'api/process';
import { useGlobalContext } from 'context/GlobalContext';
import { AccessElements } from 'helpers/app.config';

import { columns, headerToExport } from './columns';
import { constants } from './constants';
import StateFlowGraph from './components/StateFlowGraph';
import StateFlowTable from './components/StateFlowTable';

interface ModalState {
  showCreate: boolean;
  showEdit: boolean;
  showDuplicate: boolean;
  showData?: ProblemFlowSearch | null;
  idOriginal?: number | null;
}

interface DeleteModalState {
  id?: number;
  name?: string;
}

/**
 * Componente para la gestión de flujos de estados de Problemas
 *
 * @returns <StatusFlow />
 */
function StatusFlow() {
  const { t } = useTranslation('global');
  const { processApi } = useApiContext();
  const { hasPerm } = useGlobalContext();

  const translatedColumns = useMemo(
    () => genericTranslationColumns(t, columns),
    [t]
  );

  const {
    CREATE_SUCCESS,
    CREATE_ERROR,
    DELETE_SUCCESS,
    DELETE_ERROR,
    UPDATE_SUCCESS,
    UPDATE_ERROR
  } = useMemo(() => genericTranslationConstants(t, constants), [t]);

  const translatedHeaderToExport = useMemo(
    () => genericTranslationHeaders(t, headerToExport),
    [t]
  );

  const addForm = useForm({
    mode: 'all'
  });
  const editForm = useForm({
    mode: 'all'
  });

  const [openModal, setOpenModal] = useState<ModalState>({
    showCreate: false,
    showEdit: false,
    showDuplicate: false,
    showData: null,
    idOriginal: null
  });

  const [openDeleteModal, setOpenDeleteModal] =
    useState<DeleteModalState | null>(null);

  const [graphModalId, setGraphModalId] = useState<number | null>(null);
  const [graphModalName, setGraphModalName] = useState<string>('');

  const closeAddEditModal = useCallback(() => {
    setOpenModal({
      showCreate: false,
      showEdit: false,
      showDuplicate: false,
      showData: null,
      idOriginal: null
    });
    addForm.reset();
    editForm.reset();
  }, [addForm, editForm]);

  const closeDeleteModal = useCallback(() => {
    setOpenDeleteModal(null);
  }, []);

  const {
    getResponse,
    deleteResponse,
    paginationProps,
    itemsToExport,
    setParams,
    params,
    queryProps,
    handleExport
  } = useCatalog({
    getKey: () => KEYS.process.problemFlow.generalSearch(),
    getFunction: () =>
      processApi.ProblemFlow.generalSearchProblemStatusFlow({
        ...queryProps,
        ...params
      }),
    deleteFunction: (id: number) =>
      processApi.ProblemFlow.deleteProblemFlow(id),
    getExport: () =>
      processApi.ProblemFlow.generalSearchProblemStatusFlow({
        ...queryProps,
        ...params,
        isExport: true
      }),
    closeAddEditModal,
    closeDeleteModal,
    message: {
      CREATE_SUCCESS,
      CREATE_ERROR,
      DELETE_SUCCESS,
      DELETE_ERROR,
      UPDATE_SUCCESS,
      UPDATE_ERROR
    }
  });

  const {
    handleSelectItemForExport,
    itemsSelectedForExport,
    handleResetItemsForExport
  } = useSelectTableItemsToExport({
    items: getResponse?.data?.items as ProblemFlowSearch[],
    idName: 'id',
    pageNumber: paginationProps.page,
    pageSize: paginationProps.pageSize,
    getFunction: processApi.ProblemFlow.generalSearchProblemStatusFlow,
    isRefetch: false
  });

  const openModalRemove = useCallback((id: number, desc: string) => {
    setOpenDeleteModal({ id, name: desc });
  }, []);

  const onOpenModalEdit = useCallback(
    (data: ProblemFlowSearch) => {
      editForm.setValue('name', data?.name);
      editForm.setValue('enabled', data?.enabled);
      setOpenModal((prev) => ({ ...prev, showEdit: true, showData: data }));
    },
    [editForm]
  );

  const handleOpenModalEdit = useCallback((id: number) => {
    setOpenModal((prev) => ({ ...prev, showEdit: true, showData: { id } }));
  }, []);

  const extendedColumns: GridColumns = [
    ...translatedColumns,
    {
      field: '',
      headerName: t('common.commonProperties.actions'),
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (
        cellValues: GridRenderCellParams<ProblemFlowSearch, ProblemFlowSearch>
      ) => (
        <ActionsTable
          hideRemove={
            cellValues?.row?.base ||
            !hasPerm([AccessElements.ProblemFlowDelete])
          }
          onEdit={() => onOpenModalEdit(cellValues?.row)}
          onRemove={() =>
            openModalRemove(cellValues?.row?.id, cellValues?.row?.name)
          }
          childrenInTheEnd
          hideEdit
        >
          {!cellValues?.row?.base &&
            hasPerm([AccessElements.ProblemFlowUpdate]) && (
              <Tooltip
                placement="top"
                title={t('problems.statusFlows.editProblemFlow')}
              >
                <IconButton
                  onClick={() => handleOpenModalEdit(cellValues?.row?.id)}
                >
                  <AccountTreeIcon color="info" />
                </IconButton>
              </Tooltip>
            )}
          <Tooltip
            placement="top"
            title={t('problems.statusFlows.viewProblemFlow')}
          >
            <IconButton
              onClick={() => {
                setGraphModalId(cellValues?.row?.id);
                setGraphModalName(cellValues?.row?.name);
              }}
            >
              <SchemaIcon color="info" />
            </IconButton>
          </Tooltip>
        </ActionsTable>
      ),
      flex: 1,
      minWidth: 130,
      maxWidth: 130
    }
  ];

  const handleCancelModal = useCallback(() => {
    setOpenModal((prev) => ({ ...prev, showCreate: false, showEdit: false }));
    addForm.reset();
    editForm.reset();
  }, [addForm, editForm]);

  const handleDeleteProblemFlow = useCallback(() => {
    deleteResponse.mutate(openDeleteModal?.id, {
      onSuccess: async () => {
        await getResponse.refetch();
      }
    });
  }, [deleteResponse, getResponse, openDeleteModal?.id]);

  const handleOpenModalCreate = useCallback(
    (data: boolean) => {
      addForm.resetField('name');
      setOpenModal((prev) => ({ ...prev, showCreate: data }));
    },
    [addForm]
  );

  useEffect(() => {
    if (openModal?.showDuplicate) {
      handleResetItemsForExport();
    }
  }, [handleResetItemsForExport, openModal?.showDuplicate]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <GenericModal
        dialogProps={{
          open: openModal?.showCreate || openModal?.showEdit,
          title: openModal?.showCreate
            ? t('common.commonActions.registrationModal', {
                title: t('problems.statusFlows.title').toLocaleLowerCase()
              })
            : t('common.commonActions.editingModal', {
                title: t('problems.statusFlows.title').toLocaleLowerCase()
              }),
          maxWidth: 'xl'
        }}
        isMutating={false}
        onCancelClickButton={handleCancelModal}
        hideButtonAccept
        hideButtonCancel
      >
        <StateFlowTable
          handleCloseStateFlowModal={closeAddEditModal}
          isEditStateFlow={openModal?.showEdit}
          problemFlowId={openModal?.showEdit ? openModal?.showData?.id : null}
        />
      </GenericModal>

      <GenericModal
        dialogProps={{
          open: openModal?.showDuplicate,
          title: t('problems.statusFlows.modals.duplicateFlow', {
            title: t('problems.statusFlows.title').toLocaleLowerCase()
          }),
          maxWidth: 'xl'
        }}
        isMutating={false}
        onCancelClickButton={() =>
          setOpenModal((prev) => ({ ...prev, showDuplicate: false }))
        }
        hideButtonAccept
        hideButtonCancel
      >
        <StateFlowTable
          handleCloseStateFlowModal={closeAddEditModal}
          isEditStateFlow={false}
          problemFlowId={openModal?.idOriginal}
          isFromDuplication
        />
      </GenericModal>

      <ReactFlowProvider>
        <StateFlowGraph
          id={graphModalId}
          isOpen={graphModalId !== null}
          name={graphModalName}
          onClose={() => setGraphModalId(null)}
        />
      </ReactFlowProvider>

      <CustomBasicOptionsModal
        acceptSubmit={handleDeleteProblemFlow}
        btnAcceptText={t('common.commonActions.accept')}
        btnCancelText={t('common.commonActions.cancel')}
        cancelSubmit={closeDeleteModal}
        message={t('common.commonMessages.deleteConfirmation', {
          title: `${openDeleteModal?.name}`
        })}
        show={!!openDeleteModal?.id}
      />

      <CatalogLayout
        catalogFilterProps={{
          hideAddButton: !hasPerm([AccessElements.ProblemFlowCreate]),
          registerFindData: getResponse?.data?.totalCount as number,
          setOpenModal: handleOpenModalCreate,
          ...paginationProps
        }}
        childrenActionButtons={
          <Grid className="actionsButtonsContainer">
            <Box className="actionButton">
              <Button
                disabled={itemsSelectedForExport.length !== 1}
                onClick={() => {
                  const selectedFlow = (
                    itemsSelectedForExport as ProblemFlowSearch[]
                  )?.[0];
                  setOpenModal((prev) => ({
                    ...prev,
                    showDuplicate: true,
                    showData: selectedFlow,
                    idOriginal: selectedFlow?.id
                  }));
                }}
                type="button"
                variant="contained"
              >
                {t('problems.statusFlows.duplicateFlow')}
              </Button>
            </Box>
          </Grid>
        }
        handleResetItemsForExport={handleResetItemsForExport}
        hideActionButton={hasPerm([AccessElements.ProblemFlowCreate])}
        setParams={setParams}
      >
        <CatalogDataGrid
          columns={extendedColumns || []}
          filename={t('problems.statusFlows.fileName')}
          getRowId={(row: ProblemFlowSearch) => row.id}
          handleSelectItemForExport={handleSelectItemForExport}
          headerToExport={translatedHeaderToExport}
          itemsSelectedForExport={itemsSelectedForExport as ProblemFlowSearch[]}
          itemsToExport={itemsToExport as ProblemFlowSearch[]}
          loading={getResponse?.isLoading}
          onExport={handleExport}
          rowCount={getResponse?.data?.totalCount as number}
          rows={(getResponse?.data?.items as ProblemFlowSearch[]) || []}
          {...paginationProps}
        />
      </CatalogLayout>
    </Suspense>
  );
}

export default StatusFlow;
